/**
 * Dragging logs and folders around the library — the ordering maths, kept pure.
 *
 * A drop answers two questions at once: **where in the order** the row lands, and **which folder it
 * belongs to now**. Both reduce to `sortOrder` / `folderId` writes, so the whole thing is "produce
 * the new sequence, then write only the rows that actually moved".
 *
 * **Resequenced 1..N rather than given a midpoint.** Midpoints are the usual trick and they are
 * wrong for an integer column: `sortOrder` is an `Integer`, so a few dozen drops between the same
 * pair exhaust the gap and two rows collide silently. At this scale — tens of logs, a handful of
 * folders — renumbering costs at most N small PATCHes, and only for the rows whose number really
 * changed, which is usually two or three.
 *
 * Pure and file-local for the reason `libraryRows.ts` and `logTabs.ts` are: `npm test` has no DOM,
 * so a rule is only reachable if it is not inside a component.
 */

import type { ReadingFolder, ReadingLog } from "@/lib/reading";

/** What a library row drag carries — private, so a drop can tell it from a file or an image slot. */
export const LIBRARY_DRAG_TYPE = "application/x-nihongonator-library-row";

export interface LibraryDragPayload {
  kind: "log" | "folder";
  id: string;
}

export interface Reorder {
  id: string;
  sortOrder: number;
}

/** What a drop resolves to: a new order, and — for a log — the folder it now lives in. */
export interface DropPlan {
  folderId?: string | null;
  reorders: Reorder[];
}

/**
 * Move `movedId` to sit where `targetId` is, and renumber the list.
 *
 * Returns **only the rows whose `sortOrder` changed**, so a drop that resolves to nothing — onto
 * itself, or onto the row already in that position — writes nothing.
 *
 * `before` decides which side of the target the row lands on. Callers derive it from the pointer's
 * position within the target row (`dropsBefore`), so dragging upwards drops above and downwards
 * drops below — what every list does, and what a reader expects without being told.
 */
export function reorder<T extends { id: string; sortOrder: number }>(
  items: readonly T[],
  movedId: string,
  targetId: string,
  before: boolean,
): Reorder[] {
  const ordered = [...items].sort((a, b) => a.sortOrder - b.sortOrder);
  const moved = ordered.find((item) => item.id === movedId);
  if (!moved) return [];

  const without = ordered.filter((item) => item.id !== movedId);
  const targetAt = without.findIndex((item) => item.id === targetId);
  // A target that is not in this list means the caller has already decided the two belong together
  // — a log arriving from another folder, say — so it appends rather than refusing.
  const at = targetAt === -1 ? without.length : targetAt + (before ? 0 : 1);
  const next = [...without.slice(0, at), moved, ...without.slice(at)];

  const changed: Reorder[] = [];
  next.forEach((item, index) => {
    // 1-based, so a later `_next_sort` (max + 1) still lands after everything here.
    const sortOrder = index + 1;
    if (item.sortOrder !== sortOrder) changed.push({ id: item.id, sortOrder });
  });
  return changed;
}

/**
 * A log dropped **on another log**: it adopts that log's folder and takes a position beside it.
 *
 * **Filing and ordering are decided together**, because a log's position is only meaningful within
 * its folder — the library sorts each folder's contents independently. Dropping a サクラノ詩 log
 * onto a Muv-Luv one has to re-file *and* renumber, and doing those as two independent operations
 * leaves the row briefly ordered against a group it is not in.
 */
export function planLogDrop(
  logs: readonly ReadingLog[],
  movedId: string,
  targetLogId: string,
  before: boolean,
): DropPlan | null {
  const moved = logs.find((entry) => entry.id === movedId);
  const target = logs.find((entry) => entry.id === targetLogId);
  if (!moved || !target || moved.id === target.id) return null;

  const folderId = target.folderId;
  // The destination group WITH the moved log in it. Arriving from another folder it is not in that
  // list yet, so it is appended here and `reorder` then places it.
  const group = [
    ...logs.filter((entry) => entry.folderId === folderId && entry.id !== movedId),
    { ...moved, folderId },
  ];
  const reorders = reorder(group, movedId, targetLogId, before);
  // A same-folder drop that changed nothing is a no-op; a cross-folder one always writes, because
  // the folder itself moved even where the numbering happened to land the same.
  if (reorders.length === 0 && moved.folderId === folderId) return null;
  return { folderId, reorders };
}

/**
 * A log dropped **on a folder row**: filed there, and placed last.
 *
 * Last rather than first: a folder is a work and its logs are chapters, so the natural place for
 * one you have just filed is after the ones already there. Dropping onto a specific log inside the
 * folder is how you say otherwise.
 */
export function planFolderDrop(
  logs: readonly ReadingLog[],
  movedId: string,
  folderId: string | null,
): DropPlan | null {
  const moved = logs.find((entry) => entry.id === movedId);
  if (!moved || moved.folderId === folderId) return null;
  const highest = logs
    .filter((entry) => entry.folderId === folderId && entry.id !== movedId)
    .reduce((top, entry) => Math.max(top, entry.sortOrder), 0);
  return { folderId, reorders: [{ id: movedId, sortOrder: highest + 1 }] };
}

/** Folders reorder among themselves and nothing else — there is one level, so there is one list. */
export function planFolderReorder(
  folders: readonly ReadingFolder[],
  movedId: string,
  targetId: string,
  before: boolean,
): DropPlan | null {
  const reorders = planFolderReorderList(folders, movedId, targetId, before);
  return reorders.length === 0 ? null : { reorders };
}

function planFolderReorderList(
  folders: readonly ReadingFolder[],
  movedId: string,
  targetId: string,
  before: boolean,
): Reorder[] {
  if (movedId === targetId) return [];
  return reorder(folders, movedId, targetId, before);
}

/**
 * Which half of a row the pointer is in.
 *
 * Exported so the drop handler and its test agree about where the midpoint is, rather than each
 * carrying its own idea of it.
 */
export function dropsBefore(pointerY: number, rect: { top: number; height: number }): boolean {
  return pointerY < rect.top + rect.height / 2;
}
